import { FC, useEffect, useState } from 'react';
import {
  Container,
  Stepper,
  Paper,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Box,
} from '@mantine/core';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle } from '@tabler/icons-react';
import { usePatientOnboarding } from '@hooks/usePatientOnboarding';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { PatientSearch } from './components/PatientSearch';
import { PatientReview } from './components/PatientReview';
import { PatientOnboardingEncounterForm } from './components/PatientOnboardingEncounterForm';
import { EncounterCreatedConfirmation } from './components/EncounterCreatedConfirmation';

/**
 * Enum for patient onboarding workflow steps
 */
enum OnboardingStep {
  SEARCH = 0,
  PATIENT_REVIEW = 1,
  ENCOUNTER = 2,
  CONFIRMATION = 3,
}

/**
 * Main patient onboarding page
 * Multi-step process: Search → Patient Details → Encounter → Confirmation
 * @returns JSX.Element
 */
export const PatientOnboardingPage: FC = () => {
  const { t } = useTranslation('patient-onboarding');
  const {
    state,
    setPatient,
    setEncounter,
    reset,
  } = usePatientOnboarding();

  // Local state for stepper navigation
  const [activeStep, setActiveStep] = useState<OnboardingStep>(
    OnboardingStep.SEARCH
  );

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<OnboardingStep | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [patientToCheck, setPatientToCheck] = useState<Patient | undefined>(
    undefined
  );
  const { currentEncounter, loading: loadingEncounter } =
    useCurrentEncounter(patientToCheck);

  // Step handlers
  const handlePatientSelected = (patient: Patient, isNew: boolean) => {
    setPatient(patient, isNew);
    // If existing patient, check for in-progress encounter
    if (!isNew && patient.id) {
      setPatientToCheck(patient); // This will trigger useCurrentEncounter
    } else {
      setActiveStep(OnboardingStep.PATIENT_REVIEW); // Go to patient review/edit step
    }
  };

  useEffect(() => {
    if (patientToCheck && !loadingEncounter) {
      if (currentEncounter) {
        // Patient has an in-progress encounter, store it and mark as existing
        setEncounter(currentEncounter, true);
      }

      // Always go to patient review for existing patients (whether or not they have encounter)
      setActiveStep(OnboardingStep.PATIENT_REVIEW);

      // Reset the check
      setPatientToCheck(undefined);
    }
  }, [
    patientToCheck,
    currentEncounter,
    loadingEncounter,
    setEncounter,
  ]);

  const handleCreateNewPatient = () => {
    setPatient(null, true); // Set as new patient with no data
    setActiveStep(OnboardingStep.PATIENT_REVIEW); // Go directly to patient review/create step
  };

  const handlePatientSaved = (patient: Patient) => {
    setPatient(patient, state.isNewPatient);
    setHasUnsavedChanges(false);

    // If encounter already exists (in-progress), skip to confirmation
    if (state.encounter) {
      setActiveStep(OnboardingStep.CONFIRMATION);
    } else {
      setActiveStep(OnboardingStep.ENCOUNTER); // Go to encounter creation
    }
  };
  const handleContinueWithoutSave = () => {
    setHasUnsavedChanges(false);

    // If encounter already exists (in-progress), skip to confirmation
    if (state.encounter) {
      setActiveStep(OnboardingStep.CONFIRMATION);
    } else {
      setActiveStep(OnboardingStep.ENCOUNTER); // Go to encounter creation without saving
    }
  };

  const handleBackFromPatientReview = () => {
    // Check if there are unsaved changes (for both new and existing patients)
    if (hasUnsavedChanges) {
      setPendingNavigation(OnboardingStep.SEARCH);
      setShowUnsavedModal(true);
    } else {
      setActiveStep(OnboardingStep.SEARCH); // Back to search
    }
  };

  const handleStepClick = (step: number) => {
    // If on patient review step with unsaved changes, show modal (for both new and existing)
    if (
      activeStep === OnboardingStep.PATIENT_REVIEW &&
      hasUnsavedChanges &&
      step !== (OnboardingStep.PATIENT_REVIEW as number)
    ) {
      setPendingNavigation(step as OnboardingStep);
      setShowUnsavedModal(true);
    } else if (
      step <= (activeStep as number) ||
      (step === (OnboardingStep.PATIENT_REVIEW as number) && state.patient)
    ) {
      setActiveStep(step as OnboardingStep);
    }
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    if (pendingNavigation !== null) {
      setActiveStep(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleKeepEditing = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // If on patient review step with unsaved changes and click is outside the paper (for both new and existing)
    if (activeStep === OnboardingStep.PATIENT_REVIEW && hasUnsavedChanges) {
      const target = e.target as HTMLElement;
      // Check if click is outside the Paper element
      if (target.classList.contains('mantine-Container-root')) {
        e.preventDefault();
        e.stopPropagation();
        setShowUnsavedModal(true);
      }
    }
  };

  const handleBackFromEncounter = () => {
    setActiveStep(OnboardingStep.PATIENT_REVIEW); // Back to patient review
  };

  const handleEncounterCreated = (encounter: Encounter) => {
    setEncounter(encounter, false); // false = newly created encounter
    setActiveStep(OnboardingStep.CONFIRMATION); // Go to confirmation
  };

  const handleOnboardAnother = () => {
    reset();
    setActiveStep(OnboardingStep.SEARCH);
  };

  return (
    <Container size="lg" py="xl" onClick={handleContainerClick}>
      <Stack gap="xl">
        {/* Header */}
        <Box>
          <Title order={1}>{t('title')}</Title>
          <Text size="sm" c="dimmed" mt="xs">
            {t('search.title')}
          </Text>
        </Box>

        {/* Stepper */}
        <Stepper
          active={activeStep}
          onStepClick={handleStepClick}
          allowNextStepsSelect={false}
        >
          <Stepper.Step
            label={t('steps.search')}
            description={t('search.title')}
            allowStepSelect={(activeStep as number) > 0}
          >
            {/* Step 1: Search */}
          </Stepper.Step>

          <Stepper.Step
            label={t('steps.patient')}
            description={
              state.isNewPatient
                ? t('patient.titleNew')
                : t('patient.titleExisting')
            }
            allowStepSelect={
              (activeStep as number) > 1 && state.patient !== null
            }
          >
            {/* Step 2: Patient Review/Edit */}
          </Stepper.Step>

          <Stepper.Step
            label={t('steps.encounter')}
            description={
              state.isExistingEncounter
                ? t('steps.encounterExisting')
                : t('encounter.title')
            }
            allowStepSelect={false}
            color={
              state.isExistingEncounter
                ? 'orange'
                : undefined
            }
          >
            {/* Step 3: Encounter Creation */}
          </Stepper.Step>

          <Stepper.Completed>
            <div>{/* Step 4: Confirmation */}</div>
          </Stepper.Completed>
        </Stepper>

        {/* Step Content */}
        <Paper
          withBorder
          p="xl"
          radius="md"
          shadow="sm"
          style={{
            position: 'relative',
            outline:
              activeStep === OnboardingStep.PATIENT_REVIEW && hasUnsavedChanges
                ? '3px solid var(--mantine-color-orange-4)'
                : undefined,
          }}
        >
          {activeStep === OnboardingStep.SEARCH && (
            <PatientSearch
              onPatientSelected={handlePatientSelected}
              onCreateNewPatient={handleCreateNewPatient}
            />
          )}

          {activeStep === OnboardingStep.PATIENT_REVIEW &&
            (state.patient || state.isNewPatient) && (
              <PatientReview
                patient={state.patient}
                isNewPatient={state.isNewPatient}
                onPatientSaved={handlePatientSaved}
                onContinueWithoutSave={handleContinueWithoutSave}
                onDirtyStateChange={setHasUnsavedChanges}
                onBack={handleBackFromPatientReview}
              />
            )}

          {activeStep === OnboardingStep.ENCOUNTER && state.patient && (
            <PatientOnboardingEncounterForm
              patient={state.patient}
              onEncounterCreated={handleEncounterCreated}
              onBack={handleBackFromEncounter}
            />
          )}

          {activeStep === OnboardingStep.CONFIRMATION &&
            state.encounter &&
            state.patient && (
              <EncounterCreatedConfirmation
                encounter={state.encounter}
                patient={state.patient}
                onOnboardAnother={handleOnboardAnother}
                isExistingEncounter={state.isExistingEncounter}
              />
            )}
        </Paper>

        {/* Unsaved Changes Modal */}
        <Modal
          opened={showUnsavedModal}
          onClose={handleKeepEditing}
          title={
            <Group gap="xs">
              <IconAlertTriangle
                size={24}
                color="var(--mantine-color-orange-6)"
              />
              <Text fw={600}>{t('patient.unsavedChangesTitle')}</Text>
            </Group>
          }
          centered
        >
          <Stack gap="md">
            <Text>{t('patient.unsavedChangesMessage')}</Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleKeepEditing}>
                {t('patient.keepEditingButton')}
              </Button>
              <Button color="red" onClick={handleDiscardChanges}>
                {t('patient.discardChangesButton')}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
};

export default PatientOnboardingPage;
